import { Button, ButtonGroup, HStack, Stack } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Trans, useTranslation } from "react-i18next";
import { Field, BorderedBox } from "components/ui";
import {
  useCreateAppraisalMutation,
  AppraisalInput,
  useGetAssetByIdLazyQuery,
  AssetNode,
} from "graphql/queries/generated/queries";
import { ValidationProvider } from "components/form/ValidationContext";
import { AppraisalInputSchema } from "graphql/queries/generated/validation-schema";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { SelectedAsset } from "components/ui/PolicyAssetSearchBox/types";
import { useNavigate } from "react-router-dom";
import { handleMutation, toast } from "middleware/Toaster";
import { MGMT_ROUTES } from "routes/constants";
import AppraisalFields from "../shared/AppraisalFields";
import SearchAssetsDrawer from "../edit/SearchAssetsDrawer";
import NewAssetDrawer from "../edit/NewAssetDrawer";

const NewAppraisalPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const voidAsset: AssetNode = null;

  const [createAppraisal, { data, loading, error, client, called }] = useCreateAppraisalMutation({
    ...handleMutation("Appraisal has been created!"),
  });

  const [userWantsToAddAssets, setUserWantsToAddAssets] = useState(false);
  const [userWantsToCreateAssets, setUserWantsToCreateAssets] = useState<boolean>(false);
  const [choosenAssets, setChoosenAssets] = useState<string[] | []>([]);
  const [assetAdded, setAssetAdded] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [choosenAssetBigData, setChoosenAssetBigData] = useState(voidAsset);

  let data2 = data;

  /**
   * onCompleteEvent e getAsset funzionano sia per il drawer degli
   * oggetti preesistenti sia per quello della creazione di nuovi
   * oggetti
   */

  const onCompleteEvent = (assetNode: AssetNode) => {
    toast({
      title: "Appraisal not saved.",
      description: "Press the 'save' button to finalize the changes.",
      status: "warning",
      duration: 9000,
      isClosable: true,
    });

    setDisabled(false);
    setChoosenAssetBigData(assetNode);
    setAssetAdded(true);
  };

  const [getAsset, { loading: loadingAsset, error: errorAsset, data: dataAsset }] =
    useGetAssetByIdLazyQuery({
      onCompleted: (result) => {
        onCompleteEvent(result.asset as AssetNode);
      },
    });

  const addExistingAsset = async ({ gqlId }: Partial<SelectedAsset>) => {
    setChoosenAssets((prevState) => [gqlId]);

    await getAsset({
      variables: {
        id: gqlId,
      },
    });
  };

  const onOpenDrawer = () => {
    setUserWantsToAddAssets(true);
  };

  const onOpenNewAssetDrawer = () => {
    setUserWantsToCreateAssets(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const methods = useForm<AppraisalInput>({
    defaultValues: {
      appraisalStatus: "draft",
      valueEstimatedValue: {
        amount: 0.0,
        currency: "EUR",
      },
      valueCarryingValue: {
        amount: 0.0,
        currency: "EUR",
      },
    },
    resolver: yupResolver(AppraisalInputSchema()),
  });

  const addNewAsset = async ({ gqlId }: Partial<SelectedAsset>) => {
    setChoosenAssets((prevState) => [gqlId]);

    await getAsset({
      variables: {
        id: gqlId,
      },
    });
  };

  const {
    formState: { errors },
    setFocus,
  } = methods;

  useEffect(() => {
    const firstError = Object.keys(errors).reduce(
      (field, a) => ((errors as unknown as never)[field] ? field : a),
      null
    );

    try {
      if (firstError) {
        setFocus(firstError as keyof AppraisalInput);
      }
    } catch (e) {
      console.log(e);
    }
  }, [errors, setFocus]);

  const onSubmit: SubmitHandler<AppraisalInput> = async (formData, e) => {
    let selectedAppraisalAsset: string = null;

    if (assetAdded) [selectedAppraisalAsset] = choosenAssets;
    else selectedAppraisalAsset = null;

    const result = await createAppraisal({
      variables: {
        input: {
          appraisalData: {
            appraisalAsset: selectedAppraisalAsset,
            ...formData,
          },
        },
      },
    });

    data2 = result.data;

    setTimeout(() => {
      navigate(`${MGMT_ROUTES.APPRAISAL}/edit/${result.data.createAppraisal.appraisal.id}`);
    }, Number(process.env.REACT_APP_REDIRECT_TIMEOUT) ?? 1000);

    return null;
  };

  return (
    <Stack spacing="4">
      <FormProvider {...methods}>
        <ValidationProvider schema={AppraisalInputSchema()}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack spacing="4">
              <BorderedBox title={t("Asset")} subtitle="Lorem ipsum dolor sit amet">
                <HStack justifyContent="space-between" alignItems="flex-start">
                  <Field label="ID" value={assetAdded ? choosenAssetBigData.id : ""} />
                  <Field label="Title" value={assetAdded ? choosenAssetBigData.title : ""} />
                  <Field
                    label="Author"
                    value={
                      assetAdded &&
                      choosenAssetBigData.authorEntity != null &&
                      choosenAssetBigData.authorEntity.fullName
                        ? choosenAssetBigData.authorEntity.fullName
                        : ""
                    }
                  />

                  <Field
                    label="Date of creation"
                    value={
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                      assetAdded &&
                      choosenAssetBigData.actualDateAndPeriod !== null &&
                      choosenAssetBigData.actualDateAndPeriod !== ""
                        ? choosenAssetBigData.actualDateAndPeriod
                        : ""
                    }
                  />

                  <ButtonGroup pb={6} mt={2} display="block" textAlign="left" variant="outline">
                    <Button onClick={onOpenDrawer} variant="primary">
                      <Trans>Choose new object</Trans>
                    </Button>
                    <Button onClick={onOpenNewAssetDrawer} variant="primary">
                      <Trans>Create and add new object</Trans>
                    </Button>
                  </ButtonGroup>
                </HStack>
              </BorderedBox>

              <AppraisalFields
                footerData={data}
                loading={loading}
                disabled={disabled}
                errors={[error]}
                watch={methods.watch}
                asset={choosenAssetBigData}
              />
            </Stack>
          </form>
        </ValidationProvider>
      </FormProvider>

      {/**
       * Per lo meno il NewAssetDrawer DEVE stare fuori dal tag <form> (e quindi da ValidationProvider e FormProvider)
       * perche' altrimenti il submit del drawer causa il submit della form esterna.
       * Poi in generale non e' certo una buona idea annidare due form...
       */}
      <SearchAssetsDrawer
        show={userWantsToAddAssets}
        onClose={() => setUserWantsToAddAssets(false)}
        addAsset={addExistingAsset}
        choosenAssets={choosenAssets}
      />
      <NewAssetDrawer
        show={userWantsToCreateAssets}
        onClose={() => setUserWantsToCreateAssets(false)}
        addAsset={addNewAsset}
      />
    </Stack>
  );
};

export default NewAppraisalPage;
